import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
// @material-ui/core components
import { makeStyles, fade, responsiveFontSizes, createMuiTheme } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Popper from "@material-ui/core/Popper";
import Zoom from "@material-ui/core/Zoom";
// @material-ui/icons
import Notifications from "@material-ui/icons/Notifications";
import CloseRounded from "@material-ui/icons/CloseRounded";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import AssignmentIcon from '@material-ui/icons/Assignment';
// core components
import Button from "components/CustomButtons/Button.jsx";

import headerLinksStyle from "assets/jss/material-dashboard-react/components/headerLinksStyle.jsx";
import { connect } from "react-redux";
import "./Styles/Navbar.css";

import { API_POST } from "../../services/api.service";

import { Badge, Grid, Divider, Typography, Box, Avatar, Tooltip, IconButton, CircularProgress } from "@material-ui/core";
import socketIOClient from "socket.io-client";
import { initAudioContext, playNewNotificationSound } from "../../utils/audioUtils";
import PushNotificationV2 from "./PushNotificationV2";
const { REACT_APP_SERVER_URL } = process.env;
export const base_url = `//${REACT_APP_SERVER_URL}`;


// Create a custom theme for better typography
const theme = responsiveFontSizes(createMuiTheme({
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h6: {
            fontWeight: 700,
            letterSpacing: '0.02em',
        },
        subtitle1: {
            fontWeight: 600,
            letterSpacing: '0.01em',
        },
        subtitle2: {
            fontWeight: 500,
            letterSpacing: '0.01em',
        },
        body1: {
            fontWeight: 400,
            letterSpacing: '0.01em',
            lineHeight: 1.5,
        },
        body2: {
            fontWeight: 400,
            letterSpacing: '0.01em',
            lineHeight: 1.43,
        },
    },
}));

// Create styles with makeStyles
const useStyles = makeStyles(theme => ({
    ...headerLinksStyle,
    '@keyframes pulse': {
        '0%': {
            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.4)'
        },
        '70%': {
            boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)'
        },
        '100%': {
            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)'
        }
    },
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
            transform: 'translateY(-10px)'
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)'
        }
    },
    notificationButton: {
        padding: '10px',
        borderRadius: '50%',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: 'rgba(238, 226, 226, 0.08)',
            transform: 'scale(1.05)'
        },
        '&:active': {
            transform: 'scale(0.95)'
        }
    },
    pulseAnimation: {
        animation: '$pulse 2s infinite'
    },
    notificationPaper: {
        width: 380,
        maxHeight: 500,
        overflow: 'hidden',
        borderRadius: 8,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        animation: '$fadeIn 0.3s ease-out',
        border: '1px solid rgba(0, 0, 0, 0.08)'
    },
    notificationHeader: {
        padding: theme.spacing(2, 2.5),
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#fff'
    },
    notificationList: {
        maxHeight: 400,
        overflowY: 'auto',
        padding: 0,
        '&::-webkit-scrollbar': {
            width: '6px'
        },
        '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.05)'
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '3px'
        }
    },
    notificationItem: {
        padding: theme.spacing(2, 2.5),
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
        height: 'auto',
        whiteSpace: 'normal'
    },
    notificationContent: {
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%'
    },
    notificationMessage: {
        flex: 1,
        wordBreak: 'break-word',
        fontSize: 14,
        lineHeight: 1.5,
        marginLeft: theme.spacing(1.5),
        transition: 'color 0.2s ease',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%'
    },
    notificationTime: {
        fontSize: 12,
        color: '#757575',
        marginTop: 4,
        fontWeight: 400,
        letterSpacing: '0.02em',
        opacity: 0.85
    },
    notificationIcon: {
        color: '#ffffff',
        backgroundColor: '#4051B5',
        padding: 12,
        borderRadius: '50%',
        marginRight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
    },
    noNotifications: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1)
    },
    unreadIndicator: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: '#4caf50',
        position: 'absolute',
        left: -14,
        boxShadow: '0 0 4px rgba(255, 0, 0, 0.3)'
    },
    closeButton: {
        transition: 'transform 0.2s ease',
        '&:hover': {
            transform: 'rotate(90deg)'
        }
    },
    badge: {
        transition: 'all 0.3s ease'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(3)
    }
}));

const PushNotification = (props) => {
    const { user, history } = props;
    const classes = useStyles();

    // State hooks
    const [open, setOpen] = useState(false);
    const [notificationPopup, setNotificationPopup] = useState(false);
    const [notifications, setNotifications] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 760);
    const [newNotification, setNewNotification] = useState(0);
    const [socket, setSocket] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasNewNotification, setHasNewNotification] = useState(false);
    const [previousNotificationCount, setPreviousNotificationCount] = useState(0);
    const [useNewUI, setUseNewUI] = useState(false);

    // Refs
    const anchorRef = useRef(null);
    const userDetails = JSON.parse(localStorage.getItem("userLoginDetails"));

    // Handle toggle notification
    const handleToggleNotification = () => {
        // Initialize audio context on user interaction
        initAudioContext();

        setNotificationPopup(prevState => !prevState);
        setOpen(false);

        // Reset new notification indicator when opening the panel
        if (!notificationPopup) {
            setHasNewNotification(false);
        }
    };

    // Handle close notification
    const handleCloseNotification = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
        setNotificationPopup(false);
    };

    // Remove notification
    const removeNotification = async (e) => {
        if (user && user.admin) {
            await API_POST("action/update", {
                root: "notifications",
                querydata: { _id: e._id },
                body: { IsActive: false, read: 1 },
            });
            loadNotification();
        }
    };

    // Handle notification click
    const onClickNotification = async (val) => {
        if (val.prospectId) {
            history.push("/admin/ViewProspect/" + val.prospectId + "/1");
        }
    };

    // Load notifications
    const loadNotification = useCallback(async () => {
        if (user && user.admin) {
            try {
                setLoading(true);
                const registerRequest = await API_POST("action/getdata", {
                    root: "notifications",
                    con: { user: user.email, IsActive: true },
                    body: {limit:50,skip:0},
                });

                registerRequest.data = registerRequest.data.data;
                let newCount = 0;
                if (registerRequest.data && registerRequest.data.length > 0) {
                    // Check if any notification has newVersion === 1.0
                    // Count unread notifications
                    newCount = registerRequest.data.reduce((count, val) => {
                        return val.read === 0 ? count + 1 : count;
                    }, 0);

                    // Check if there are new notifications
                    if (newCount > previousNotificationCount) {
                        setHasNewNotification(true);


                        playNewNotificationSound();


                        // Update previous count for next comparison
                        setPreviousNotificationCount(newCount);
                    }

                    // Sort notifications by createdOn date (newest first)
                    const sortedNotifications = [...registerRequest.data].sort((a, b) => {
                        const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
                        const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
                        return dateB - dateA; // Descending order (newest first)
                    });

                    setNotifications(sortedNotifications);
                    setNewNotification(newCount);
                } else {
                    setNotifications(null);
                    setNotificationPopup(false);
                    setOpen(false);
                    setNewNotification(0);
                    setHasNewNotification(false);
                }
            } catch (error) {
                console.error("Error loading notifications:", error);
            } finally {
                setLoading(false);
            }
        }
    }, [user, newNotification]);

    // Setup socket connection
    const setupSocketConnection = useCallback(() => {
        if (user && user.admin) {
            try {
                const _base_url = base_url.replace('/api', '');
                const newSocket = socketIOClient(`${_base_url}`, {
                    transports: ["websocket"],
                    path: "/api/io/socket.io",
                    secure: true,
                    forceNew: true
                });

                newSocket.emit("new user", user.email);

                newSocket.on("read_messages", () => {
                    loadNotification();
                });

                newSocket.on("connect", () => {
                    setSocket(newSocket);
                });

                newSocket.on("disconnect", (reason) => {
                    setTimeout(() => {
                        newSocket.emit("reconnect", user.email);
                    }, 1000);
                    setSocket(null);
                });

                return newSocket;
            } catch (error) {
                console.error("Socket connection error:", error);
                return null;
            }
        }
    }, [user, loadNotification]);

    // Effect for initial load and socket setup
    useEffect(() => {
        const timer = setTimeout(() => {
            loadNotification();
        }, 3000);

        const newSocket = setupSocketConnection();

        // Initialize audio context
        initAudioContext();

        // Cleanup function
        return () => {
            clearTimeout(timer);
            if (newSocket) {
                newSocket.disconnect();
            }
        };
    }, [loadNotification, setupSocketConnection]);





    // Format notification time
    const formatNotificationTime = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString();
    };

    // Render notification icon based on type
    const getNotificationIcon = (type) => {
        // You can extend this to show different icons based on notification type
        switch (type) {
            case 'new_prospect':
                return <PersonAddIcon style={{ fontSize: 26, color: '#ffffff' }} />
            case 'duplicate_prospect':
                return <PersonAddIcon style={{ fontSize: 26, color: '#ffffff' }} />
            case 'repeat_prospect':
                return <PersonAddIcon style={{ fontSize: 26, color: '#ffffff' }} />
            case 'callback_reminder':
                return <CalendarTodayIcon style={{ fontSize: 26, color: '#ffffff' }} />
            case 'support-ticket':
                return <AssignmentIcon style={{ fontSize: 26, color: '#ffffff' }} />
            case 'alert':
                return <NotificationsActiveIcon style={{ fontSize: 26, color: '#ffffff' }} />
            default:
                return <NotificationsNoneIcon style={{ fontSize: 26, color: '#ffffff' }} />;
        }
    };


    return (
        <>
            {socket && socket.connected && (
                <>
               
                    <Tooltip title="Notifications" TransitionComponent={Zoom} arrow>
                        <IconButton
                            ref={anchorRef}
                            aria-label="Notifications"
                            aria-haspopup="true"
                            onClick={handleToggleNotification}
                            className={`${classes.notificationButton} ${hasNewNotification ? classes.pulseAnimation : ''}`}
                        >
                            <Badge
                                badgeContent={newNotification}
                                color="secondary"
                                invisible={newNotification === 0}
                                className={classes.badge}
                                overlap="circular"
                            >
                                <Notifications
                                    style={{
                                        fontSize: 22,
                                        color: hasNewNotification ? '#f44336' : '#ffffff',
                                        transition: 'color 0.3s ease'
                                    }}
                                />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    <Popper
                        open={notificationPopup}
                        anchorEl={anchorRef.current}
                        placement="bottom-end"
                        style={{ zIndex: 1200 }}
                        transition
                        disablePortal
                        className={classNames({
                            [classes.popperClose]: !notificationPopup,
                            "notification-popper": true,
                            [classes.pooperNav]: true
                        })}
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin:
                                        placement === "bottom" ? "right top" : "right bottom"
                                }}
                            >
                                <Paper className={classes.notificationPaper} elevation={6}>
                                    <div className={classes.notificationHeader}>
                                        <Typography variant="h6" component="h6" style={{
                                            fontWeight: 700,
                                            fontSize: 20,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: '#212121'
                                        }}>
                                            NOTIFICATIONS
                                        </Typography>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>

                                            {notifications && notifications.length > 0 && (
                                                <Typography variant="subtitle2" color="secondary" style={{
                                                    fontWeight: 600,
                                                    letterSpacing: '0.02em',
                                                    fontSize: 14
                                                }}>
                                                    {newNotification} unread
                                                </Typography>
                                            )}
                                        </div>
                                    </div>

                                    <ClickAwayListener onClickAway={handleCloseNotification}>
                                        <MenuList className={classes.notificationList}>
                                            {loading ? (
                                                <div className={classes.loadingContainer}>
                                                    <CircularProgress size={28} color="primary" />
                                                </div>
                                            ) : notifications && notifications.length > 0 ? (
                                                notifications.map((notification, index) => {
                                                    // Check if this specific notification should use new UI
                                                    const useNewUIForThisNotification = notification.version === "1.0";
                                                    
                                                    return (
                                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={notification._id || index}>
                                                            {useNewUIForThisNotification ? (
                                                                // Use PushNotificationV2 component for new UI
                                                                <PushNotificationV2 
                                                                    notification={notification}
                                                                    index={index}
                                                                    onClickNotification={onClickNotification}
                                                                    removeNotification={removeNotification}
                                                                    formatNotificationTime={formatNotificationTime}
                                                                    getNotificationIcon={getNotificationIcon}
                                                                    classes={classes}
                                                                />
                                                            ) : (
                                                                // Original UI for notifications without newVersion === 1.0
                                                                <MenuItem
                                                                    className={classes.notificationItem}
                                                                    onClick={() => onClickNotification(notification)}
                                                                    style={{
                                                                        backgroundColor: notification.read === 0 ? 'rgba(0, 0, 0, 0.02)' : 'transparent'
                                                                    }}
                                                                >
                                                                    <div className={classes.notificationContent} style={{ position: 'relative' }}>
                                                                        {notification.read === 0 && (
                                                                            <div className={classes.unreadIndicator} />
                                                                        )}
                                                                        <Box className={classes.notificationIcon}>
                                                                            {getNotificationIcon(notification.type)}
                                                                        </Box>
                                                                        <div className={classes.notificationMessage}>
                                                                            <Typography
                                                                                variant="body1"
                                                                                style={{
                                                                                    fontWeight: 400,
                                                                                    color: notification.read === 0 ? '#212121' : '#424242',
                                                                                    marginBottom: 4,
                                                                                    letterSpacing: '0.01em',
                                                                                    lineHeight: 1.6,
                                                                                    fontSize: 14,
                                                                                    WebkitFontSmoothing: 'antialiased',
                                                                                    MozOsxFontSmoothing: 'grayscale',
                                                                                    whiteSpace: 'normal',
                                                                                    wordWrap: 'break-word',
                                                                                    display: 'block',
                                                                                    maxWidth: '100%'
                                                                                }}
                                                                            >
                                                                                {notification.isHtml ? (
                                                                                    <div dangerouslySetInnerHTML={{ __html: notification.message }} />
                                                                                ) : (
                                                                                    notification.message
                                                                                )}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                className={classes.notificationTime}
                                                                                style={{
                                                                                    color: '#757575',
                                                                                    WebkitFontSmoothing: 'antialiased',
                                                                                    MozOsxFontSmoothing: 'grayscale',
                                                                                    fontSize: 13
                                                                                }}
                                                                            >   {notification.createdOn && formatNotificationTime(notification.createdOn)}
                                                                                
                                                                            </Typography>
                                                                        </div>
                                                                        <Tooltip title="Remove" arrow TransitionComponent={Zoom}>
                                                                            <IconButton
                                                                                size="small"
                                                                                className={classes.closeButton}
                                                                                style={{ color: '#F44336' }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    removeNotification(notification);
                                                                                }}
                                                                            >
                                                                                <CloseRounded fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </div>
                                                                </MenuItem>
                                                            )}
                                                        </Zoom>
                                                    );
                                                })
                                            ) : (
                                                <div className={classes.noNotifications}>
                                                    <NotificationsNoneIcon style={{ fontSize: 40, opacity: 0.5, marginBottom: 8 }} />
                                                    <Typography
                                                        variant="body1"
                                                        color="textSecondary"
                                                        style={{
                                                            WebkitFontSmoothing: 'antialiased',
                                                            MozOsxFontSmoothing: 'grayscale',
                                                            letterSpacing: '0.01em',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        No notifications found
                                                    </Typography>
                                                </div>
                                            )}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </>
            )}
        </>
    );
};
const mapStateToProps = (state) => {
    return {};
};

export default connect(
    mapStateToProps
)(PushNotification);
